import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../services/admin';
import { PermissionService } from '../../../core/authorization/permission';
import { GeneralSettingsFormData } from '../../../shared/models/general-settings-form-data';
import { Observable } from 'rxjs';
import { IsrRule } from '../../../shared/models/isr-rule';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RetentionIvaRule } from '../../../shared/models/retention-iva-rule';

@Component({
    selector: 'app-general-settings',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatSlideToggleModule],
    templateUrl: './general-settings.html',
    styleUrl: './general-settings.css'
})
export class GeneralSettings implements OnInit {
    generalSettingsForm       : FormGroup;
    canUpdateGeneralSettings$ : Observable<boolean>;

    constructor(private fb: FormBuilder, private adminService: AdminService, private permissionService: PermissionService) {
        this.generalSettingsForm = this.fb.group({
            ivaRate           : [12, [Validators.required, Validators.min(0)]],
            isrRules          : this.fb.array([]),
            retentionIVARules : this.fb.array([])
        });

        this.canUpdateGeneralSettings$ = this.permissionService.hasPermission('generalSettings.update');
    }

    ngOnInit(): void {
        this.loadGeneralSettings();
    }

    async loadGeneralSettings(): Promise<void> {
        const settings = await this.adminService.getGeneralSettings();

        if(settings && settings.isrRules) {
            this.isrRulesArray.clear();

            settings.isrRules.forEach(rule => {
                this.isrRulesArray.push(this.createISRRuleGroup(rule));
            });
        }

        if(settings) {
            if(settings.ivaRate !== undefined && settings.ivaRate !== null) {
                this.generalSettingsForm.get('ivaRate')?.setValue(settings.ivaRate);
            }

            this.retentionIVARulesArray.clear();

            if(settings.retentionIVARules && Array.isArray(settings.retentionIVARules)) {
                settings.retentionIVARules.forEach(rule => {
                    this.retentionIVARulesArray.push(this.createRetentionIVARuleGroup(rule));
                });
            }
        }
    }

    createISRRuleGroup(rule: IsrRule = { minQuota: 0, percentage: 0, fixedDiscount: 0, subtractMinQuotaForExcess: true }): FormGroup {
        return this.fb.group({
            minQuota: [rule.minQuota, [Validators.required, Validators.min(0)]],
            percentage: [rule.percentage, [Validators.required, Validators.min(0)]],
            fixedDiscount: [rule.fixedDiscount, [Validators.required, Validators.min(0)]],
            subtractMinQuotaForExcess: [rule.subtractMinQuotaForExcess ?? true]
        });
    }

    addISRRule(): void {
        this.isrRulesArray.push(this.createISRRuleGroup());
    }

    removeISRRule(index: number): void {
        this.isrRulesArray.removeAt(index);
    }

    createRetentionIVARuleGroup(rule: RetentionIvaRule = { minPassages: 0, retentionPct: 0 }): FormGroup {
        return this.fb.group({
            minPassages  : [rule.minPassages, [Validators.required, Validators.min(0)]],
            retentionPct : [rule.retentionPct, [Validators.required, Validators.min(0)]]
        });
    }

    addRetentionIVARule(): void {
        this.retentionIVARulesArray.push(this.createRetentionIVARuleGroup());
    }

    removeRetentionIVARule(index: number): void {
        this.retentionIVARulesArray.removeAt(index);
    }

    async onSubmit(): Promise<void> {
        this.generalSettingsForm.markAllAsTouched();

        if(this.generalSettingsForm.valid) {
            const formData: GeneralSettingsFormData = this.generalSettingsForm.value;
            const success = await this.adminService.updateGeneralSettings(formData);

            if(success) {
                this.loadGeneralSettings();
            }
        }
    }

    get isrRulesArray(): FormArray {
        return this.generalSettingsForm.get('isrRules') as FormArray;
    }

    get retentionIVARulesArray(): FormArray {
        return this.generalSettingsForm.get('retentionIVARules') as FormArray;
    }
}
